/**
 * The Sold out and Recent sales views: when the list is showing what sold rather than what is on
 * the shelf, and how one row's sale figures read. Pure, so both are testable without rendering.
 */
import { inventoryCopy } from './inventoryCopy'
import type { InventoryUrlFilters } from './inventoryUrlState'
import type { InventoryProduct } from './inventoryTypes'

const copy = inventoryCopy.sales

export const SOLD_WINDOWS = ['7', '30', '90'] as const

export type InventorySalesFocus = { title: string; body: string; windowDays: number | null }

/** What the current filters are asking about, or null for the ordinary stock list. */
export function salesFocusFor(filters: Pick<InventoryUrlFilters, 'stock' | 'sold'>): InventorySalesFocus | null {
  if (filters.sold) {
    const days = Number(filters.sold)
    return filters.stock === 'soldout'
      ? { title: copy.restockListTitle, body: copy.restockListBody(days), windowDays: days }
      : { title: copy.recentTitle, body: copy.recentBody(days), windowDays: days }
  }
  if (filters.stock === 'soldout') return { title: copy.soldOutTitle, body: copy.soldOutBody, windowDays: null }
  return null
}

export const isSoldOut = (product: Pick<InventoryProduct, 'availableQuantity'>): boolean => product.availableQuantity <= 0

/** Nothing free to sell, but units are on the shelf for an open order: not gone yet. */
export const heldForOrders = (product: Pick<InventoryProduct, 'availableQuantity' | 'reservedQuantity'>): number =>
  isSoldOut(product) && product.reservedQuantity > 0 ? product.reservedQuantity : 0

/** A row swaps Total Value (always $0.00 at quantity 0) for what it sold. */
export const showsSales = (product: Pick<InventoryProduct, 'availableQuantity'>, windowDays: number | null): boolean =>
  windowDays !== null || isSoldOut(product)

const dayStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

/** "Today", "Yesterday", "12 Oct", then the year once it is not this one. */
export function formatSoldDate(iso: string, locale: string, now: Date = new Date()): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const days = Math.round((dayStart(now) - dayStart(date)) / 86400000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  })
}

/** The units a row is ranked on: in the window when there is one, otherwise all time. */
export function unitsSoldFor(sales: InventoryProduct['sales'], windowDays: number | null): number {
  if (!sales) return 0
  return windowDays !== null ? (sales.unitsInWindow ?? 0) : sales.unitsSold
}
